import axios from 'axios';

import { createApiClient } from '@/api/http-client';
import { handleSessionExpired } from '@/api/session-expiry';
import { config } from '@/config';
import {
  MicrosoftOboRefreshResponseDTO,
  SessionResponseDTO,
  SsoCallbackResponseDTO,
  SsoStartResponseDTO,
} from '@/models/auth.mapper';
import { createLogger } from '@/utils/logging';

const logger = createLogger('AuthAPI', 'api');

/** Default axios timeout — session probes should stay bounded. */
const DEFAULT_AUTH_TIMEOUT_MS = 30_000;
/**
 * SSO flows call the API which may exchange codes with Microsoft Entra; cold starts or
 * network latency can exceed short client timeouts and surface as false "sign-in failed".
 */
const SSO_TOKEN_EXCHANGE_TIMEOUT_MS = 120_000;

/**
 * Extracts the human-readable message from an API error, preferring the backend's `detail` field.
 */
const extractApiErrorMessage = (error: unknown): string => {
  if (!axios.isAxiosError(error)) {
    return error instanceof Error ? error.message : 'Request failed';
  }

  const detail = error.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim().length > 0) {
    return detail;
  }

  return error.message;
};

const authApiClient = createApiClient({
  baseURL: config.apiUrl,
  timeout: DEFAULT_AUTH_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
});

authApiClient.interceptors.request.use(
  (cfg) => {
    logger.debug('Request', { method: cfg.method, url: cfg.url });
    return cfg;
  },
  (error) => {
    logger.error('Request error', error);
    return Promise.reject(error);
  },
);

authApiClient.interceptors.response.use(
  (response) => {
    logger.debug('Response', { status: response.status, url: response.config.url });
    return response;
  },
  (error) => {
    handleSessionExpired(error);
    const message = extractApiErrorMessage(error);
    logger.error('Response error', { status: error.response?.status, message });
    return Promise.reject(new Error(message));
  },
);

/**
 * Initiates the SSO authorization flow for the given provider.
 * POST /auth/sso/start
 *
 * @param provider - Provider identifier (e.g., "entra").
 * @returns The authorization URL to redirect the user to.
 */
export const startSso = async (provider: string): Promise<SsoStartResponseDTO> => {
  const response = await authApiClient.post<SsoStartResponseDTO>(
    '/auth/sso/start',
    { provider },
    { timeout: SSO_TOKEN_EXCHANGE_TIMEOUT_MS },
  );
  return response.data;
};

/**
 * Exchanges the IdP authorization code for a platform access token.
 * POST /auth/sso/callback
 *
 * @param code - The authorization code from the IdP redirect.
 * @param state - The CSRF state parameter returned by the IdP.
 * @returns The session access token and authenticated user.
 */
export const completeSsoCallback = async (
  code: string,
  state: string,
): Promise<SsoCallbackResponseDTO> => {
  const response = await authApiClient.post<SsoCallbackResponseDTO>(
    '/auth/sso/callback',
    {
      code,
      state,
    },
    { timeout: SSO_TOKEN_EXCHANGE_TIMEOUT_MS },
  );
  return response.data;
};

/**
 * Validates the current session via the httpOnly cookie and returns the authenticated user.
 * GET /auth/session
 *
 * The cookie is sent automatically (withCredentials is set globally on the client).
 * The backend echoes the validated JWT in the response body so the frontend can
 * restore its in-memory token state after a page refresh.
 */
export const getSession = async (): Promise<SessionResponseDTO> => {
  const response = await authApiClient.get<SessionResponseDTO>('/auth/session');
  return response.data;
};

/**
 * Clears the httpOnly session cookie.
 * POST /auth/logout
 */
export const logoutSession = async (): Promise<void> => {
  await authApiClient.post('/auth/logout', {});
};

/**
 * Obtains a fresh Microsoft Entra OBO assertion for Graph API access.
 * POST /auth/microsoft/obo/refresh
 *
 * The session cookie is sent automatically.
 *
 * @returns A new short-lived OBO assertion token.
 */
export const refreshMicrosoftOboAssertion = async (): Promise<MicrosoftOboRefreshResponseDTO> => {
  const response = await authApiClient.post<MicrosoftOboRefreshResponseDTO>(
    '/auth/microsoft/obo/refresh',
    {},
    { timeout: SSO_TOKEN_EXCHANGE_TIMEOUT_MS },
  );
  return response.data;
};
